// FEAT-16 — the rules a candidate reads before the test: what we ask of them,
// what is prohibited, and that it is recorded. The wording is Emilia's (Sheet
// #151 — her "Test rules" Google Doc, read 2026-10-06), verbatim, line for line.
//
// PURE, and built from what `/preview` already says about THIS test, so the
// screen can never promise a check the recruiter switched off (FEAT-25).
//
// Two limits on what may be written here:
//   - It is NOT consent and NOT the privacy notice. Nothing in this file says
//     what is stored, for how long or who else sees it — the step links to the
//     notice instead. The three consent ticks on the form are legal copy and
//     are not restated or replaced by any of this.
//   - A check that is off is never mentioned, in either direction: no "we
//     record…" for it and no "this test is not monitored" either. Her lines
//     about the cursor and about pasting describe the activity check (FEAT-25:
//     the cursor leaving the page and pasted text are what it records), so
//     they go with it.
//
// Still placeholder (hers to word — owed.md): the monitoring lines for what her
// text does not cover (fullscreen, the camera, the code replay) and the closing
// line of a test that records nothing at all.
import type { AnticheatConfig } from "./anticheatConfig";

export interface TestRulesInput {
  /** The checks this test runs (FEAT-25) — `/preview`'s copy. */
  checks: AnticheatConfig;
  /** The test has at least one coding question (the replay line needs one). */
  hasCode: boolean;
  /** FEAT-31: this test stores a code replay (`/preview`'s `code_replay`).
   *  Not one of the FEAT-25 checks — it can be on with every check off. */
  codeReplay?: boolean;
}

export interface TestRules {
  /** "We ask you to:" */
  asked: string[];
  /** "During the test, the following is prohibited:" */
  prohibited: string[];
  /** One line per check that runs AND that her text does not already cover.
   *  Empty = the step shows no monitoring block at all. */
  monitoring: string[];
  /** Her closing line while something is recorded on this test. */
  closing: string;
}

export function buildTestRules(input: TestRulesInput): TestRules {
  const { checks } = input;

  const asked = [
    "complete the test on your own",
    "stay in the test window for the entire duration",
  ];
  if (checks.activity) asked.push("keep your cursor within the test tab");

  const prohibited = [
    "using AI tools",
    "switching to other tabs",
    "leaving the test window",
  ];
  if (checks.activity) {
    prohibited.push(
      "copying and pasting content",
      "moving your cursor outside the test tab",
    );
  }

  // What her text does not cover but THIS test records: fullscreen exits, the
  // camera, the code replay (FEAT-31's legal redlines count on that line being
  // said before the candidate starts). ⚠ Placeholder wording (owed.md).
  const monitoring: string[] = [];
  if (checks.fullscreen) {
    monitoring.push(
      "The test runs in fullscreen, and each time you leave it is recorded.",
    );
  }
  if (checks.camera) {
    monitoring.push(
      "Your camera stays on to check that one person is taking the test.",
    );
  }
  if (input.codeReplay && input.hasCode) {
    monitoring.push(
      "How your code changes while you write it is recorded, including text you paste or delete in the code editor.",
    );
  }

  // Nothing here disqualifies anyone by itself: the company reads what was
  // recorded and decides. With every check off and no replay nothing is
  // recorded, so her line would be untrue — the one placeholder left here.
  const closing =
    checks.activity || monitoring.length > 0
      ? "Each of these events is recorded and visible to the recruiter."
      : "If the company that invited you finds that these rules were broken, it may disqualify you.";

  return { asked, prohibited, monitoring, closing };
}
